import type { PropsWithChildren, HTMLAttributes } from 'react'

import { AtSignIcon } from 'lucide-react'

import { cn } from '@/utils'

import type { MentionDto } from '@/entities/mentions'

import { getMentionColors } from './mentions.utils'

interface MentionChipLayoutProps extends HTMLAttributes<HTMLDivElement> {
  color?: string
  iconBackgroundColor?: string
  backgroundColor?: string
  iconColor?: string
  name: string
  ref?: React.Ref<HTMLElement>
}

function MentionChipLayout({
  name,
  color = '#fff',
  backgroundColor = 'var(--color-gray-800)',
  iconBackgroundColor = 'var(--color-gray-600)',
  iconColor = '#fff',
  style,
  className,
  ref,
  ...spanProps
}: PropsWithChildren<MentionChipLayoutProps>) {
  return (
    <span
      ref={ref}
      className={cn(
        'rounded-sm overflow-hidden inline-flex items-stretch text-left select-none',
        className,
      )}
      style={{
        color,
        backgroundColor,
        ...style,
      }}
      {...spanProps}
    >
      <span
        className="shrink-0 px-1 inline-flex items-center"
        style={{
          color: iconColor,
          backgroundColor: iconBackgroundColor,
        }}
      >
        <AtSignIcon size={12} />
      </span>

      <span
        className="grow px-1 border border-l-0 rounded-r-sm"
        style={{
          borderColor: iconBackgroundColor,
        }}
      >
        {name}
      </span>
    </span>
  )
}

export function MentionChip({
  mention,
  className,
  ref,
  ...props
}: {
  mention: MentionDto
  className?: string
  ref?: React.Ref<HTMLElement>
}) {
  const { color, backgroundColor, iconBackgroundColor } = getMentionColors(
    mention.color,
  )

  return (
    <MentionChipLayout
      ref={ref}
      color={color}
      backgroundColor={backgroundColor}
      iconBackgroundColor={iconBackgroundColor}
      iconColor={color}
      name={mention.name}
      className={className}
      {...props}
    />
  )
}

export function MentionChipLoading() {
  return (
    <MentionChipLayout
      color={'var(--color-muted-foreground)'}
      iconColor={'var(--color-muted-foreground)'}
      name={'Loading...'}
      className="cursor-wait"
    />
  )
}
